import { Preferences } from '@capacitor/preferences';

/** The flag the app used before this bookkeeping existed: 'true' | 'false' | absent. */
const LEGACY_WIZARD_KEY = 'wizardCompleted';

/**
 * The revision the last release before this bookkeeping effectively shipped — language
 * and push, i.e. revision 1 of the catalogue.
 *
 * Raise this only if steps are added to that release retroactively, which cannot happen.
 */
const BOOTSTRAP_REVISION = 1;

/**
 * Derives a starting revision for an installation that has no wizard state yet, and this
 * is the only place that ever looks at the legacy flag.
 *
 * It runs once per installation. Afterwards the recorded revision carries itself forward
 * and nothing here is consulted again — which is the point: the legacy key can answer
 * "was this app here before *this* release", but it could never answer that question for
 * the release after next.
 *
 * The flag is deliberately left in storage rather than removed, so a rollback to a version
 * that still reads it does not put an existing user through the wizard again.
 *
 * - absent  → nobody has ever started this app: a fresh install (`null`).
 * - 'true'  → an existing install that completed the old wizard: revision 1.
 * - 'false' → an existing install that abandoned it. Revision 0, so the setup steps come
 *             back *and* the announcement applies — that user is both, and the version
 *             comparison handles it without a special case.
 */
export const readBootstrapRevision = async (): Promise<number | null> => {
  const { value } = await Preferences.get({ key: LEGACY_WIZARD_KEY });
  if (value === null) return null;
  return value === 'true' ? BOOTSTRAP_REVISION : 0;
};

